import React, { useState } from "react";
import "./ConsuntiviList.css";
import { useCollection } from "../../../hooks/useCollection";
import { useFirestore } from "../../../hooks/useFirestore";
import { database } from "../../../firebase/config";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import Select from "react-select";
import { downloadConsuntivoPdf } from "../../../stampe/generatori/consuntivoPdf";

const parseNumberLoose = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  let raw = String(value).trim();
  if (!raw) return 0;

  raw = raw.replace(/€/g, "").replace(/\s+/g, "");
  const hasDot = raw.includes(".");
  const hasComma = raw.includes(",");

  if (hasDot && hasComma) {
    const lastDot = raw.lastIndexOf(".");
    const lastComma = raw.lastIndexOf(",");
    raw =
      lastComma > lastDot
        ? raw.replace(/\./g, "").replace(/,/g, ".")
        : raw.replace(/,/g, "");
  } else if (hasComma) {
    raw = raw.replace(/,/g, ".");
  }

  raw = raw.replace(/[^0-9.-]/g, "");
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDirittoChiamataTotale = (item) => {
  if (!item || item.applicato === false) return 0;

  const valore = parseNumberLoose(item.valore);
  if (valore > 0) return valore;

  const qta = parseNumberLoose(item.qta ?? item.quantita ?? 1);
  const prezzoUnitario = parseNumberLoose(
    item.prezzo_unitario ??
      item.prezzoUnitario ??
      item.diritto_chiamata_prezzo_unitario ??
      item.diritto_chiamata,
  );

  return qta * prezzoUnitario;
};

export default function ConsuntiviList() {
  const { documents: consuntivi, error } = useCollection("consuntivi");
  const { deleteDocument, updateDocument } = useFirestore("consuntivi");

  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [editingConsuntivo, setEditingConsuntivo] = useState(null);
  const [editFormData, setEditFormData] = useState({
    materiali: [],
    manodopera: [],
    dirittiChiamata: [],
  });
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Prevent body scroll when editing modal is open
  React.useEffect(() => {
    if (editingConsuntivo) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [editingConsuntivo]);

  // --- MONTH OPTIONS ---
  const monthOptions = React.useMemo(() => {
    if (!consuntivi) return [];
    const uniqueMonths = new Set();
    consuntivi.forEach((doc) => {
      if (doc.data_creazione) {
        const date = doc.data_creazione.toDate
          ? doc.data_creazione.toDate()
          : new Date(doc.data_creazione);
        const label = format(date, "MMMM yyyy", { locale: it });
        const value = format(date, "yyyy-MM");
        // Capitalize first letter of month
        const capitalizedLabel = label.charAt(0).toUpperCase() + label.slice(1);
        uniqueMonths.add(JSON.stringify({ value, label: capitalizedLabel }));
      }
    });
    return Array.from(uniqueMonths)
      .map((s) => JSON.parse(s))
      .sort((a, b) => b.value.localeCompare(a.value));
  }, [consuntivi]);

  // --- FILTERED CONSUNTIVI ---
  const filteredConsuntivi = React.useMemo(() => {
    if (!consuntivi) return [];
    if (!selectedMonth) return consuntivi;

    return consuntivi.filter((doc) => {
      if (!doc.data_creazione) return false;
      const date = doc.data_creazione.toDate
        ? doc.data_creazione.toDate()
        : new Date(doc.data_creazione);
      const docMonth = format(date, "yyyy-MM");
      return docMonth === selectedMonth.value;
    });
  }, [consuntivi, selectedMonth]);

  // --- SELECTION & EXPORT ---
  const handleSelectAll = (e) => {
    if (e.target.checked && filteredConsuntivi) {
      setSelectedIds(filteredConsuntivi.map((c) => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const handleExportCSV = async () => {
    if (selectedIds.length === 0) return;
    setIsExporting(true);
    try {
      const selectedConsuntivi = consuntivi.filter((c) =>
        selectedIds.includes(c.id),
      );
      const allDocIds = new Set();
      selectedConsuntivi.forEach((c) => {
        if (c.documenti_inclusi) {
          c.documenti_inclusi.forEach((id) => allDocIds.add(id));
        }
      });

      const docIdsArray = Array.from(allDocIds);
      const docsMap = {};

      // Fetch dei documenti originali per recuperare Oggetto e Rif Bolla
      const snapshots = await Promise.all(
        docIdsArray.map((id) =>
          database.collection("bolle_vendita").doc(id).get(),
        ),
      );

      snapshots.forEach((snap) => {
        if (snap.exists) docsMap[snap.id] = { id: snap.id, ...snap.data() };
      });

      const csvRows = [];
      // Header CSV
      csvRows.push([
        "Oggetto",
        "Importo",
        "Rif Bolla",
        "Cliente",
        "Settore",
        "Codice",
      ]);

      const escape = (val) => `"${String(val || "").replace(/"/g, '""')}"`;

      for (const consuntivo of selectedConsuntivi) {
        const linkedDocs = (consuntivo.documenti_inclusi || [])
          .map((id) => docsMap[id])
          .filter(Boolean);

        if (linkedDocs.length > 0) {
          // Una riga per ogni intervento collegato
          linkedDocs.forEach((doc) => {
            // Calcola il totale specifico per questo documento (intervento)
            const docMateriali = (consuntivo.materiali || []).filter(
              (m) => m.docId === doc.id,
            );
            const docManodopera = (consuntivo.manodopera || []).filter(
              (m) => m.docId === doc.id,
            );
            const docDiritti = (consuntivo.dirittiChiamata || []).filter(
              (d) => d.docId === doc.id && d.applicato,
            );

            const totMat = docMateriali.reduce(
              (acc, m) => acc + Number(m.quantita) * Number(m.prezzo_unitario),
              0,
            );
            const totMan = docManodopera.reduce(
              (acc, m) => acc + Number(m.totale_ore) * Number(m.tariffa_oraria),
              0,
            );
            const totDir = docDiritti.reduce(
              (acc, d) => acc + Number(d.valore),
              0,
            );

            const docTotal = totMat + totMan + totDir;

            csvRows.push([
              escape(doc.oggetto_intervento || ""),
              escape(docTotal.toFixed(2)),
              escape(doc.numero_documento || ""),
              escape(consuntivo.cliente),
              escape(consuntivo.settore?.nome || ""),
              escape(consuntivo.settore?.codice || ""),
            ]);
          });
        } else {
          // Caso senza documenti collegati: una sola riga con i dati del consuntivo
          csvRows.push([
            escape(""),
            escape(consuntivo.totale_complessivo),
            escape(""),
            escape(consuntivo.cliente),
            escape(consuntivo.settore?.nome || ""),
            escape(consuntivo.settore?.codice || ""),
          ]);
        }

        // Riga vuota di separazione tra consuntivi
        csvRows.push([]);
      }

      const csvContent = csvRows.map((e) => e.join(",")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `consuntivi_export_${format(new Date(), "yyyyMMdd_HHmm")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Export Error:", err);
      alert("Errore durante l'esportazione CSV.");
    } finally {
      setIsExporting(false);
    }
  };

  // --- DOWNLOAD PDF ---
  const handleDownload = async (consuntivo) => {
    setIsDownloading(true);
    try {
      // 1. Recupera i documenti originali (Bolle)
      const originalDocs = [];
      const docIds = consuntivo.documenti_inclusi || [];

      // Cerchiamo i documenti nella collezione bolle_vendita (principale)
      // Se necessario, si potrebbe cercare anche altrove, ma per ora assumiamo bolle_vendita
      const promises = docIds.map((id) =>
        database.collection("bolle_vendita").doc(id).get(),
      );

      const snapshots = await Promise.all(promises);
      snapshots.forEach((snap) => {
        if (snap.exists) {
          originalDocs.push({ id: snap.id, ...snap.data() });
        } else {
          // Fallback se il documento originale è stato cancellato
          console.warn(`Documento originale ${snap.id} non trovato.`);
          originalDocs.push({
            id: snap.id,
            numero_documento: "Doc Cancellato",
            data_documento: "",
            oggetto_intervento: "Documento originale non trovato",
          });
        }
      });

      // 2. Prepara i dati nel formato atteso da downloadConsuntivoPdf
      // consuntivoData deve avere { materiali: [], manodopera: [] }
      const consuntivoData = {
        materiali: consuntivo.materiali || [],
        manodopera: consuntivo.manodopera || [],
        dirittiChiamata: consuntivo.dirittiChiamata || [],
        periodo_mese: consuntivo.periodo_mese || "",
        periodo_anno: consuntivo.periodo_anno || "",
      };

      // 3. Genera e scarica
      await downloadConsuntivoPdf({
        consuntivoData,
        documents: originalDocs,
        selectedIds: docIds,
        onReady: async (blob, fileName) => {
          // Usa window.showSaveFilePicker se disponibile, altrimenti download classico
          if (window.showSaveFilePicker) {
            try {
              const handle = await window.showSaveFilePicker({
                suggestedName: fileName,
                types: [
                  {
                    description: "PDF",
                    accept: { "application/pdf": [".pdf"] },
                  },
                ],
              });
              const writable = await handle.createWritable();
              await writable.write(blob);
              await writable.close();
            } catch (err) {
              console.log("Salvataggio annullato o fallito", err);
            }
          } else {
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      });
    } catch (err) {
      console.error("Errore durante il download:", err);
      alert("Errore durante la generazione del PDF.");
    } finally {
      setIsDownloading(false);
    }
  };

  // --- DELETE ---
  const handleDelete = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questo consuntivo?")) {
      await deleteDocument(id);
    }
  };

  // --- EDIT ---
  const handleEditClick = (consuntivo) => {
    setEditingConsuntivo(consuntivo);

    // Ordina i materiali per docRef
    const sortedMateriali = (consuntivo.materiali || []).sort((a, b) => {
      const refA = a.docRef || "";
      const refB = b.docRef || "";
      // Confronto alfanumerico (es. "Bolla 2" vs "Bolla 10")
      return refA.localeCompare(refB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

    setEditFormData({
      materiali: JSON.parse(JSON.stringify(sortedMateriali)),
      manodopera: JSON.parse(JSON.stringify(consuntivo.manodopera || [])),
      dirittiChiamata: JSON.parse(
        JSON.stringify(consuntivo.dirittiChiamata || []),
      ),
    });
  };

  const handleCloseEdit = () => {
    setEditingConsuntivo(null);
    setEditFormData({ materiali: [], manodopera: [], dirittiChiamata: [] });
  };

  const handleEditChange = (section, index, field, value) => {
    setEditFormData((prev) => {
      const newList = [...prev[section]];
      newList[index] = { ...newList[index], [field]: value };
      return { ...prev, [section]: newList };
    });
  };

  const handleSaveEdit = async () => {
    if (!editingConsuntivo) return;

    // Ricalcola i totali
    const totaleMateriali = editFormData.materiali.reduce(
      (acc, m) =>
        acc +
        parseNumberLoose(m.quantita) * parseNumberLoose(m.prezzo_unitario),
      0,
    );
    const totaleManodopera = editFormData.manodopera.reduce(
      (acc, m) =>
        acc +
        parseNumberLoose(m.totale_ore) * parseNumberLoose(m.tariffa_oraria),
      0,
    );
    const totaleDirittiChiamata = editFormData.dirittiChiamata.reduce(
      (acc, item) => acc + getDirittoChiamataTotale(item),
      0,
    );
    const totaleComplessivo =
      totaleMateriali + totaleManodopera + totaleDirittiChiamata;

    const updatedData = {
      materiali: editFormData.materiali,
      manodopera: editFormData.manodopera,
      dirittiChiamata: editFormData.dirittiChiamata,
      totale_materiali: totaleMateriali.toFixed(2),
      totale_manodopera: totaleManodopera.toFixed(2),
      totale_diritti_chiamata: totaleDirittiChiamata.toFixed(2),
      totale_complessivo: totaleComplessivo.toFixed(2),
    };

    await updateDocument(editingConsuntivo.id, updatedData);
    handleCloseEdit();
  };

  return (
    <div className="consuntivi-list-container">
      <div className="list-header">
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <h1>Archivio Consuntivi</h1>
          <div style={{ minWidth: "250px", zIndex: 10 }}>
            <Select
              options={monthOptions}
              value={selectedMonth}
              onChange={setSelectedMonth}
              placeholder="Filtra per mese..."
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        </div>
        {selectedIds.length > 0 && (
          <button
            className="btn-export"
            onClick={handleExportCSV}
            disabled={isExporting}
          >
            {isExporting
              ? "Esportazione..."
              : `Esporta CSV (${selectedIds.length})`}
          </button>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {!consuntivi && <p>Caricamento...</p>}
      {consuntivi && filteredConsuntivi.length === 0 && (
        <p>Nessun consuntivo trovato.</p>
      )}

      {consuntivi && filteredConsuntivi.length > 0 && (
        <table className="consuntivi-table">
          <thead>
            <tr>
              <th style={{ width: "40px", textAlign: "center" }}>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={
                    filteredConsuntivi &&
                    filteredConsuntivi.length > 0 &&
                    filteredConsuntivi.every((doc) =>
                      selectedIds.includes(doc.id),
                    )
                  }
                />
              </th>
              <th>Data</th>
              <th>Cliente</th>
              <th>Luogo</th>
              <th>Totale (€)</th>
              <th>Doc. Inclusi</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredConsuntivi.map((doc) => (
              <tr
                key={doc.id}
                className={selectedIds.includes(doc.id) ? "selected-row" : ""}
              >
                <td style={{ textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(doc.id)}
                    onChange={() => handleSelectRow(doc.id)}
                  />
                </td>
                <td>
                  {doc.data_creazione
                    ? format(doc.data_creazione.toDate(), "dd/MM/yyyy HH:mm")
                    : "N/A"}
                </td>
                <td>{doc.cliente || "Sconosciuto"}</td>
                <td>{doc.luogo_lavoro || "-"}</td>
                <td>€ {doc.totale_complessivo}</td>
                <td>
                  {doc.documenti_inclusi ? doc.documenti_inclusi.length : 0}
                </td>
                <td className="actions-cell">
                  <div className="actions-wrapper">
                    <button
                      className="btn-icon download"
                      onClick={() => handleDownload(doc)}
                      disabled={isDownloading}
                      title="Scarica PDF"
                    >
                      <span className="material-icons">download</span>
                    </button>
                    <button
                      className="btn-icon edit"
                      onClick={() => handleEditClick(doc)}
                      title="Modifica"
                    >
                      <span className="material-icons">edit</span>
                    </button>
                    <button
                      className="btn-icon delete"
                      onClick={() => handleDelete(doc.id)}
                      title="Elimina"
                    >
                      <span className="material-icons">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal Modifica */}
      {editingConsuntivo && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Modifica Consuntivo</h2>
              <button className="close-modal-btn" onClick={handleCloseEdit}>
                &times;
              </button>
            </div>

            <div className="edit-section-container">
              <div className="edit-section">
                <h3>Materiali</h3>
                {editFormData.materiali.length === 0 ? (
                  <p>Nessun materiale.</p>
                ) : (
                  <table
                    className="edit-table"
                    style={{ width: "100%", tableLayout: "fixed" }}
                  >
                    <thead>
                      <tr>
                        <th style={{ width: "50%" }}>Descrizione</th>
                        <th style={{ width: "10%" }}>Unità</th>
                        <th style={{ width: "10%" }}>Qtà</th>
                        <th style={{ width: "15%" }}>Prezzo Unit. (€)</th>
                        <th style={{ width: "15%" }}>Rif. Doc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editFormData.materiali.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              value={item.descrizione || ""}
                              style={{ width: "100%" }}
                              onChange={(e) =>
                                handleEditChange(
                                  "materiali",
                                  index,
                                  "descrizione",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={item.unita || ""}
                              style={{ width: "100%" }}
                              onChange={(e) =>
                                handleEditChange(
                                  "materiali",
                                  index,
                                  "unita",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={item.quantita}
                              style={{ width: "100%" }}
                              onChange={(e) =>
                                handleEditChange(
                                  "materiali",
                                  index,
                                  "quantita",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              value={item.prezzo_unitario}
                              style={{ width: "100%" }}
                              onChange={(e) =>
                                handleEditChange(
                                  "materiali",
                                  index,
                                  "prezzo_unitario",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <span style={{ fontSize: "0.85em", color: "#666" }}>
                              {item.docRef || "-"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="edit-section">
                <h3>Manodopera</h3>
                {editFormData.manodopera.length === 0 ? (
                  <p>Nessuna manodopera.</p>
                ) : (
                  <table className="edit-table">
                    <thead>
                      <tr>
                        <th>Tecnico</th>
                        <th>Ore</th>
                        <th>Tariffa (€)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editFormData.manodopera.map((item, index) => (
                        <tr key={index}>
                          <td>{item.nome_tecnico}</td>
                          <td>
                            <input
                              type="number"
                              step="0.5"
                              value={item.totale_ore}
                              onChange={(e) =>
                                handleEditChange(
                                  "manodopera",
                                  index,
                                  "totale_ore",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="1.00"
                              value={item.tariffa_oraria}
                              onChange={(e) =>
                                handleEditChange(
                                  "manodopera",
                                  index,
                                  "tariffa_oraria",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="edit-section">
                <h3>Diritti di Chiamata</h3>
                {editFormData.dirittiChiamata.length === 0 ? (
                  <p>Nessun diritto di chiamata.</p>
                ) : (
                  <table className="edit-table">
                    <thead>
                      <tr>
                        <th>Applicato</th>
                        <th>Q.ta</th>
                        <th>Prezzo Unit. (€)</th>
                        <th>Totale (€)</th>
                        <th>Rif. Doc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editFormData.dirittiChiamata.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="checkbox"
                              checked={item.applicato !== false}
                              onChange={(e) =>
                                handleEditChange(
                                  "dirittiChiamata",
                                  index,
                                  "applicato",
                                  e.target.checked,
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={item.qta ?? item.quantita ?? 1}
                              onChange={(e) =>
                                handleEditChange(
                                  "dirittiChiamata",
                                  index,
                                  "qta",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={
                                item.prezzo_unitario ??
                                item.prezzoUnitario ??
                                item.diritto_chiamata_prezzo_unitario ??
                                item.diritto_chiamata ??
                                ""
                              }
                              onChange={(e) =>
                                handleEditChange(
                                  "dirittiChiamata",
                                  index,
                                  "prezzo_unitario",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>€ {getDirittoChiamataTotale(item).toFixed(2)}</td>
                          <td>
                            <span style={{ fontSize: "0.85em", color: "#666" }}>
                              {item.docRef || "-"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={handleCloseEdit}>
                Annulla
              </button>
              <button className="btn-save" onClick={handleSaveEdit}>
                Salva Modifiche
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
